import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, ChevronRight, MessageCircle, Mail, 
  AlertTriangle, Trash2
} from 'lucide-react';
import api from '../../api/axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { MONTH_NAMES, STATUS_CONFIG, getWhatsAppLink, formatPhoneDisplay } from './constants';

export const AdminCalendar = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState([]);
  const [dayBookings, setDayBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('month');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  useEffect(() => {
    fetchCalendarData();
  }, [selectedDate]);

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth();
      
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0);
      
      const response = await api.get(`/admin/calendar?start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate.toISOString().split('T')[0]}`);
      setCalendarData(response.data);
    } catch (error) {
      toast.error('Error al cargar el calendario');
    } finally {
      setLoading(false);
    }
  };

  const fetchDayBookings = async (date) => {
    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await api.get(`/admin/calendar/day/${dateStr}`);
      setDayBookings(response.data);
    } catch (error) {
      toast.error('Error al cargar las reservas del día');
    }
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking?.booking_id) return;
    
    setCancelLoading(true);
    try {
      const response = await api.delete(`/admin/bookings/${selectedBooking.booking_id}?refund_credit=true`);
      toast.success(`Reserva cancelada. ${response.data.credit_refunded ? 'Se devolvió 1 crédito al usuario.' : ''}`);
      setShowCancelConfirm(false);
      setSelectedBooking(null);
      fetchCalendarData();
      if (viewMode === 'day') {
        fetchDayBookings(selectedDate);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar la reserva');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleDayClick = async (day) => {
    const newDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
    setSelectedDate(newDate);
    setViewMode('day');
    await fetchDayBookings(newDate);
  };

  const handlePrevMonth = () => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1));
    setViewMode('month');
  };

  const handleNextMonth = () => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1));
    setViewMode('month');
  };

  const getDaysInMonth = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getBookingsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarData.filter(b => b.date.startsWith(dateStr));
  };

  if (loading && viewMode === 'month') {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header del calendario */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={handlePrevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h3 className="text-xl font-bold">
            {MONTH_NAMES[selectedDate.getMonth()]} {selectedDate.getFullYear()}
          </h3>
          <Button variant="outline" size="icon" onClick={handleNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={viewMode === 'month' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setViewMode('month')}
          >
            Mes
          </Button>
          <Button 
            variant={viewMode === 'day' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => {
              setViewMode('day');
              fetchDayBookings(selectedDate);
            }}
          >
            Día
          </Button>
        </div>
      </div>

      {viewMode === 'month' ? (
        /* Vista de Mes */
        <div className="grid grid-cols-7 gap-1">
          {dayNames.map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
              {day}
            </div>
          ))}
          
          {getDaysInMonth().map((day, idx) => {
            const bookings = getBookingsForDay(day);
            const confirmedCount = bookings.filter(b => b.status === 'confirmed').length;
            const pendingCount = bookings.filter(b => b.status === 'pending').length;
            const isToday = day && new Date().toDateString() === new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day).toDateString();
            
            return (
              <div
                key={idx}
                onClick={() => day && handleDayClick(day)}
                className={`min-h-[80px] p-2 rounded-lg border transition-colors ${
                  day ? 'cursor-pointer hover:bg-white/10 border-white/10' : 'border-transparent'
                } ${isToday ? 'bg-primary/20 border-primary' : 'bg-white/5'}`}
              >
                {day && (
                  <>
                    <p className={`text-sm font-medium ${isToday ? 'text-primary' : ''}`}>{day}</p>
                    {bookings.length > 0 && (
                      <div className="mt-1 space-y-1">
                        {confirmedCount > 0 && (
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span className="text-xs text-green-400">{confirmedCount}</span>
                          </div>
                        )}
                        {pendingCount > 0 && (
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                            <span className="text-xs text-yellow-400">{pendingCount}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* Vista de Día */
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" size="sm" onClick={() => setViewMode('month')}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Volver al mes
            </Button>
            <h4 className="text-lg font-semibold">
              {selectedDate.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </h4>
          </div>
          
          <div className="grid gap-2">
            {dayBookings.map((slot, idx) => (
              <div 
                key={idx}
                className={`p-4 rounded-lg border transition-colors ${
                  slot.available 
                    ? 'bg-white/5 border-white/10' 
                    : slot.status === 'confirmed'
                      ? 'bg-green-500/10 border-green-500/30 cursor-pointer hover:bg-green-500/20'
                      : slot.status === 'cancelled'
                        ? 'bg-red-500/10 border-red-500/30 opacity-50'
                        : 'bg-yellow-500/10 border-yellow-500/30 cursor-pointer hover:bg-yellow-500/20'
                }`}
                onClick={() => slot.user && setSelectedBooking(slot)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-lg font-mono font-bold w-16">{slot.time_slot}</span>
                    {slot.available ? (
                      <span className="text-muted-foreground">Disponible</span>
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          slot.status === 'confirmed' ? 'bg-green-500/20' : 'bg-yellow-500/20'
                        }`}>
                          <span className="font-bold text-sm">
                            {slot.user?.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold">{slot.user?.name}</p>
                          <p className="text-sm text-muted-foreground">{slot.user?.email}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {!slot.available && slot.user && (
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        slot.user?.plan === 'socio' ? 'bg-purple-500/20 text-purple-300' :
                        slot.user?.plan === 'inicial' ? 'bg-blue-500/20 text-blue-300' :
                        'bg-gray-500/20 text-gray-300'
                      }`}>
                        {slot.user?.plan === 'none' ? 'Sin Plan' : slot.user?.plan}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        slot.status === 'confirmed' ? 'bg-green-500/20 text-green-300' :
                        slot.status === 'cancelled' ? 'bg-red-500/20 text-red-300' :
                        'bg-yellow-500/20 text-yellow-300'
                      }`}>
                        {slot.status === 'confirmed' ? 'Confirmada' : slot.status === 'cancelled' ? 'Cancelada' : 'Pendiente'}
                      </span>
                      
                      {slot.user?.whatsapp_number && (
                        <a
                          href={getWhatsAppLink(slot.user.whatsapp_number, slot.user.name)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="p-2 rounded-full bg-green-500/20 hover:bg-green-500/30 transition-colors"
                          title="Enviar WhatsApp"
                        >
                          <MessageCircle className="h-4 w-4 text-green-400" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal de detalle de reserva */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => { setSelectedBooking(null); setShowCancelConfirm(false); }}>
          <div className="bg-background border border-white/10 rounded-lg w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Detalle de Reserva</h3>
              <Button variant="ghost" size="sm" onClick={() => { setSelectedBooking(null); setShowCancelConfirm(false); }}>✕</Button>
            </div>
            
            <div className="space-y-4">
              {/* Info del usuario */}
              <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
                <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">
                    {selectedBooking.user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-lg font-bold">{selectedBooking.user?.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedBooking.user?.email}</p>
                </div>
              </div>

              {/* Detalles */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-xs text-muted-foreground">Horario</p>
                  <p className="font-bold">{selectedBooking.time_slot}</p>
                </div>
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-xs text-muted-foreground">Estado</p>
                  <p className={`font-bold ${
                    selectedBooking.status === 'confirmed' ? 'text-green-400' :
                    selectedBooking.status === 'cancelled' ? 'text-red-400' : 'text-yellow-400'
                  }`}>
                    {selectedBooking.status === 'confirmed' ? 'Confirmada' : 
                     selectedBooking.status === 'cancelled' ? 'Cancelada' : 'Pendiente'}
                  </p>
                </div>
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-xs text-muted-foreground">Plan</p>
                  <p className="font-bold capitalize">{selectedBooking.user?.plan || 'Sin plan'}</p>
                </div>
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-xs text-muted-foreground">Créditos</p>
                  <p className="font-bold">{selectedBooking.user?.credits || 0}</p>
                </div>
              </div>

              {/* WhatsApp */}
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">WhatsApp</p>
                <div className="flex items-center justify-between">
                  <p className="font-mono">{formatPhoneDisplay(selectedBooking.user?.whatsapp_number)}</p>
                  {selectedBooking.user?.whatsapp_number && (
                    <a
                      href={getWhatsAppLink(selectedBooking.user.whatsapp_number, selectedBooking.user.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg transition-colors"
                    >
                      <MessageCircle className="h-4 w-4 text-green-400" />
                      <span className="text-sm text-green-400">Enviar mensaje</span>
                    </a>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Email</p>
                <div className="flex items-center justify-between">
                  <p>{selectedBooking.user?.email}</p>
                  <a
                    href={`mailto:${selectedBooking.user?.email}`}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors"
                  >
                    <Mail className="h-4 w-4 text-blue-400" />
                    <span className="text-sm text-blue-400">Enviar email</span>
                  </a>
                </div>
              </div>

              {/* Status del usuario */}
              {selectedBooking.user?.status && (
                <div className="p-4 bg-white/5 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2">Clasificación del Usuario</p>
                  <span className={`px-3 py-1 rounded text-sm ${
                    STATUS_CONFIG[selectedBooking.user.status]?.color || 'bg-gray-500/20 text-gray-300'
                  }`}>
                    {STATUS_CONFIG[selectedBooking.user.status]?.label || selectedBooking.user.status}
                  </span>
                </div>
              )}

              {/* Botón de Cancelar Reserva */}
              {selectedBooking.status !== 'cancelled' && (
                <div className="pt-4 border-t border-white/10">
                  {!showCancelConfirm ? (
                    <Button 
                      variant="destructive" 
                      className="w-full"
                      onClick={() => setShowCancelConfirm(true)}
                      data-testid="cancel-booking-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Cancelar Reserva
                    </Button>
                  ) : (
                    <div className="space-y-3 p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                      <p className="text-sm text-red-300 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        ¿Estás seguro de cancelar esta reserva?
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Se devolverá 1 crédito al usuario automáticamente.
                      </p>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          onClick={() => setShowCancelConfirm(false)} 
                          className="flex-1"
                          disabled={cancelLoading}
                        >
                          No, mantener
                        </Button>
                        <Button 
                          variant="destructive" 
                          onClick={handleCancelBooking}
                          disabled={cancelLoading}
                          className="flex-1"
                          data-testid="confirm-cancel-btn"
                        >
                          {cancelLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            'Sí, cancelar'
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
